<template>
    <div id="resource-status-dashboard">
        <!-- Filters Sidebar -->
        <b-sidebar right shadow bg-variant="white" width="320px" v-model="filtersOpen" backdrop>
            <template #header>
                <div class="d-flex justify-content-between align-items-center w-100 px-1 py-1">
                    <span class="font-weight-bold">Filtros</span>
                    <span style="cursor: pointer" @click="filtersOpen = false">
                        <feather-icon icon="XIcon" size="18" />
                    </span>
                </div>
            </template>

            <div class="filters-card">
                <div class="filter">
                    <label class="filter-label">Grupo</label>
                    <b-form-select v-model="filters.groupId" :options="groupOptions" size="sm"
                        @change="onGroupChange" />
                </div>

                <div class="filter">
                    <label class="filter-label">Centro de Trabalho</label>
                    <b-form-select v-model="filters.workcenterId" :options="workcenterOptions" size="sm"
                        :disabled="!filters.groupId" @change="onWorkcenterChange" />
                </div>

                <div class="filter">
                    <label class="filter-label">Recurso</label>
                    <b-form-select v-model="filters.resourceId" :options="resourceOptions" size="sm"
                        :disabled="!filters.workcenterId" />
                </div>

                <div class="filter">
                    <label class="filter-label">Status</label>
                    <b-form-select v-model="filters.statusId" :options="statusOptions" size="sm" />
                </div>
            </div>

            <template #footer>
                <div class="d-flex justify-content-end px-1 pb-1">
                    <b-button size="sm" class="mr-50" variant="outline-secondary"
                        @click="clearFilters">Limpar</b-button>
                    <b-button size="sm" variant="primary" @click="filtersOpen = false">Aplicar</b-button>
                </div>
            </template>
        </b-sidebar>

        <div class="header-block d-flex align-items-center justify-content-between">
            <div class="page-title">Status dos Recursos</div>
            <div class="d-flex align-items-center">
                <b-button variant="outline-brown" size="sm" class="mr-1 btn-filters" @click="toggleFullscreen">
                    <feather-icon :icon="isFullscreen ? 'MinimizeIcon' : 'MaximizeIcon'" class="mr-50" />
                    {{ isFullscreen ? 'Sair da Tela Cheia' : 'Tela Cheia' }}
                </b-button>
                <b-button class="btn-filters" variant="outline-brown" size="sm" @click="filtersOpen = true">
                    <feather-icon icon="FilterIcon" class="mr-50" />
                    Filtros
                </b-button>
            </div>
        </div>

        <div class="body__cards">
            <div class="cards">
                <ResourceStatusCard v-for="resource in filteredResourceList" :key="resource.resourceId"
                    :resource="resource" :siteId="currentSite" @selectCard="handleSelectResourceCard"
                    @changeFavorite="handleChangeFavorite" />
            </div>
        </div>

        <div v-if="isFullscreen" class="fullscreen-footer text-center mt-4">
            <small class="text-muted">
                COPYRIGHT © 2026 Indsiders Tecnologia. Todos os direitos reservados.
            </small>
        </div>

        <div v-if="loading" class="text-center p-5">
            <b-spinner variant="primary" label="Loading..."></b-spinner>
        </div>
    </div>
</template>

<script>
import { mapMutations, mapGetters } from "vuex";
import { BSpinner, BSidebar, BButton, BFormSelect } from "bootstrap-vue";
import ResourceStatusCard from "./ResourceStatusCard.vue";

export default {
    components: {
        ResourceStatusCard,
        BSpinner,
        BSidebar,
        BButton,
        BFormSelect,
    },
    data() {
        return {
            loading: false,
            filtersOpen: false,
            isFullscreen: false,
            filters: {
                groupId: null,
                workcenterId: null,
                resourceId: null,
                statusId: null
            },
            allResources: [],
            groupList: [],
            workcenterList: [],
            statusList: [],
            currentSite: this.$cookies.get("userInfo").currentSite,
        };
    },
    computed: {
        ...mapGetters("app", ["windowWidth"]),

        filteredWorkcenters() {
            if (!this.filters.groupId) return [];
            return this.workcenterList.filter(w => w.groupId === this.filters.groupId);
        },

        groupOptions() {
            return [
                { value: null, text: 'Todos' },
                ...this.groupList.map(g => ({ value: g.id, text: g.name }))
            ];
        },

        workcenterOptions() {
            const wcs = this.filters.groupId
                ? this.workcenterList.filter(w => w.groupId === this.filters.groupId)
                : [];
            return [
                { value: null, text: 'Todos' },
                ...wcs.map(w => ({ value: w.id, text: w.name }))
            ];
        },

        resourceOptions() {
            if (!this.filters.workcenterId) return [{ value: null, text: 'Todos' }];
            const list = this.allResources.filter(r => r.workcenterId === this.filters.workcenterId);
            const uniqueMap = new Map();
            list.forEach(r => {
                if (!uniqueMap.has(r.resourceId)) {
                    uniqueMap.set(r.resourceId, { value: r.resourceId, text: r.resourceName });
                }
            });
            return [
                { value: null, text: 'Todos' },
                ...Array.from(uniqueMap.values())
            ];
        },

        statusOptions() {
            return [
                { value: null, text: 'Todos' },
                ...(this.statusList || []).map(s => ({ value: s.id, text: s.name }))
            ];
        },

        filteredResourceList() {
            let list = this.allResources;

            if (this.filters.groupId) {
                list = list.filter(r => r.groupId === this.filters.groupId);
            }

            if (this.filters.workcenterId) {
                list = list.filter(r => r.workcenterId === this.filters.workcenterId);
            }

            if (this.filters.resourceId !== null) {
                list = list.filter(r => r.resourceId === this.filters.resourceId);
            }

            if (this.filters.statusId !== null) {
                list = list.filter(r => r.status && r.status.statusId === this.filters.statusId);
            }

            return list;
        }
    },
    mounted() {
        this.fetchAllData();
    },
    methods: {
        ...mapMutations("app", ["UPDATE_FLAG_SHOW_OVERLAY"]),

        toggleFullscreen() {
            const elem = document.querySelector('.app-content');

            if (!this.isFullscreen) {
                if (elem.requestFullscreen) {
                    elem.requestFullscreen();
                } else if (elem.webkitRequestFullscreen) { /* Safari */
                    elem.webkitRequestFullscreen();
                } else if (elem.msRequestFullscreen) { /* IE11 */
                    elem.msRequestFullscreen();
                }
                this.isFullscreen = true;
                elem.classList.add('fullscreen-mode');
            } else {
                if (document.exitFullscreen) {
                    document.exitFullscreen();
                } else if (document.webkitExitFullscreen) { /* Safari */
                    document.webkitExitFullscreen();
                } else if (document.msExitFullscreen) { /* IE11 */
                    document.msExitFullscreen();
                }
                this.isFullscreen = false;
                elem.classList.remove('fullscreen-mode');
            }
        },

        async fetchAllData() {
            this.loading = true;
            this.UPDATE_FLAG_SHOW_OVERLAY(true);
            try {
                // 1. Fetch Groups
                const groupsRes = await this.$http2.get(`/api/mysfc/operator/groups/${this.currentSite}`);
                this.groupList = groupsRes.data.data;

                // 1.5 Fetch Status List (Context)
                const contextRes = await this.$http2.get(`/api/mysfc/operation-progress/context?siteId=${this.currentSite}`);
                this.statusList = contextRes.data.data.status;

                // 2. Fetch Workcenters
                const workcenterPromises = this.groupList.map(g =>
                    this.$http2.get(`/api/mysfc/operator/workcenters/${g.id}`).then(res => {
                        const wcs = res.data.data || [];
                        return wcs.map(w => ({ ...w, groupId: g.id }));
                    })
                );
                const workcentersArrays = await Promise.all(workcenterPromises);
                this.workcenterList = workcentersArrays.flat();

                // 3. Fetch Resources for all workcenters
                // Note: The aggregated endpoint logic was removed because we need rich data from operator/resources

                // Iterate workcenters and fetch rich resource data
                const resourcePromises = this.workcenterList.map(wc =>
                    this.$http2.get(`/api/mysfc/operator/resources/${wc.id}`).then(res => {
                        const resources = res.data || [];
                        const group = this.groupList.find(g => g.id === wc.groupId);

                        return resources.map(r => ({
                            ...r,
                            workcenterId: wc.id,
                            groupId: wc.groupId,
                            workcenterName: wc.name,
                            groupName: group ? group.name : ''
                        }));
                    })
                );
                const resourcesArrays = await Promise.all(resourcePromises);
                let allRichResources = resourcesArrays.flat();

                this.allResources = allRichResources;

                // 4. Fetch OEE/Performance data
                // if (this.allResources.length > 0) {
                //     const resourceIds = this.allResources.map((r) => r.resourceId).join(",");
                //     await this.$http2
                //         .get(`/api/oee/calculate?ResourcesIds=${resourceIds}&SiteId=${this.currentSite}`)
                //         .then((oeeRes) => {
                //             const oeeByResource = oeeRes.data.data.oeeByResource;
                //             this.allResources = this.allResources.map((resource) => {
                //                 const oee = oeeByResource.find(
                //                     (item) => item.resourceId === resource.resourceId
                //                 );
                //                 if (oee) {
                //                     return {
                //                         ...resource,
                //                         perf: oee.performanceComponent * 100
                //                     };
                //                 }
                //                 return resource;
                //             });
                //         })
                //         .catch((err) => console.error(err));
                // }

            } catch (error) {
                console.error("Error fetching dashboard data", error);
            } finally {
                this.loading = false;
                this.UPDATE_FLAG_SHOW_OVERLAY(false);
            }
        },

        onGroupChange() {
            this.filters.workcenterId = null;
            this.filters.resourceId = null;
        },

        onWorkcenterChange() {
            this.filters.resourceId = null;
        },

        handleSelectResourceCard(resource) {
            this.$router.push({
                name: "operator-point-resource",
                params: {
                    prefix: this.$route.params.prefix,
                    resource_id: resource.resourceId,
                    order_id: resource.orderId,
                    material_id: resource.materialId,
                    max_orders: resource.maxOrders,
                    current_order: 1
                }
            });
        },

        handleChangeFavorite(resourceId) {
            this.UPDATE_FLAG_SHOW_OVERLAY(true);
            this.$http2
                .post(`/api/mysfc/resource/${resourceId}/favorite?siteId=${this.currentSite}`)
                .then(() => {
                    const res = this.allResources.find(r => r.resourceId === resourceId);
                    if (res) res.isFavorite = !res.isFavorite;
                    this.UPDATE_FLAG_SHOW_OVERLAY(false);
                });
        },

        clearFilters() {
            this.filters = {
                groupId: null,
                workcenterId: null,
                resourceId: null,
                statusId: null
            };
        }
    }
};
</script>

<style lang="scss">
.b-sidebar-footer {
    .btn-primary {
        background-color: #974900 !important;
        border-color: #974900 !important;

        &:hover {
            background-color: #7d3b00 !important;
            border-color: #7d3b00 !important;
        }
    }
}
</style>

<style lang="scss" scoped>
#resource-status-dashboard {
    .header-block {
        padding: 10px 14px;
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    .page-title {
        font-size: 18px;
        font-weight: 700;
        color: #4c4541;
    }

    .btn-filters {
        border-color: #974900 !important;
        color: #974900 !important;

        &:hover {
            background-color: #974900 !important;
            color: #fff !important;
        }
    }

    .filters-card {
        background: #fff;
        border: 1px solid #e9ecef;
        border-radius: 6px;
        padding: 12px;

        .filter+.filter {
            margin-top: 8px;
        }

        .filter-label {
            display: block;
            font-weight: 600;
            font-size: 12px;
            margin-bottom: 6px;
        }
    }

    .body__cards {
        .cards {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
            gap: 16px;
        }
    }


}

@media (min-width: 1400px) {
    #resource-status-dashboard .body__cards .cards {
        grid-template-columns: repeat(5, 1fr);
    }
}

@media (min-width: 1200px) and (max-width: 1399px) {
    #resource-status-dashboard .body__cards .cards {
        grid-template-columns: repeat(4, 1fr);
    }
}

.app-content.fullscreen-mode {
    background-color: #f8f8f8;
    overflow-y: auto;
    padding: 20px;
    height: 100vh;
    width: 100vw;
    display: flex;
    flex-direction: column;

    .body__cards {
        flex: 1;
    }
}

@media (max-height: 800px) {
    .app-content.fullscreen-mode {
        padding: 8px;
    }

    #resource-status-dashboard .header-block {
        padding: 10px 8px;
        margin-bottom: 0 !important;

        .page-title {
            font-size: 16px;
        }

        .btn-filters {
            font-size: 12px;
            padding: 2px 6px;
        }
    }
}
</style>